import React, { useState, useEffect } from "react";
import {
  getQuizQuestions, getQuizHistory,
  getSessionResult, getChapterReview, submitAnswer, startMocKExamSession,
  getOpenQuizSessions, resumeQuizSession,
} from '../../rtk/thunks/quizThunk'
import { clearQuizError, setMockExamTimeRemaining, setNextQuestion } from "../../rtk/slices/quizSlice";
import { useDispatch, useSelector } from "react-redux";
import { getCourseDetails } from "../../rtk/thunks/courseThunk";
import useResponsive from "../../hooks/useResponsive";
import QuestionBody from "../../components/QuestionBody";
import ContentGuard from "../../components/ContentGuard";

const bestExamStatus = (history, predicate) => {
  const attempts = (Array.isArray(history) ? history : []).filter(
    (item) => item?.status === "completed" && predicate(item)
  );
  if (!attempts.length) return { label: "Not Attempted", tone: "none" };
  const best = attempts.reduce((top, item) =>
    Number(item.score_percent || 0) > Number(top.score_percent || 0) ? item : top
  );
  return best.passed
    ? { label: "Pass", tone: "pass" }
    : { label: "Fail", tone: "fail" };
};

const statusColor = (tone) => {
  if (tone === "pass") return "#177A38";
  if (tone === "fail") return "#B42318";
  return "#6B7C93";
};

const findOpenSession = (openSessions, sessionType, extra = {}) =>
  (Array.isArray(openSessions) ? openSessions : []).find((item) => {
    if (item.session_type !== sessionType) return false;
    if (extra.domainId && Number(item.domain_id) !== Number(extra.domainId)) return false;
    if (extra.domainOrder && Number(item.domain_order) !== Number(extra.domainOrder)) return false;
    if (extra.bankSet && Number(item.mock_exam_id) !== Number(extra.bankSet)) return false;
    return true;
  });

const dualActionRow = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
  justifyContent: "center",
};

const secondaryBtn = {
  background: "#FFFFFF",
  color: "#0D355C",
  border: "1px solid #D0D5DD",
};

// ====================================================================
// SUB-COMPONENT: The main landing page view
// ====================================================================
const QuestionBankLanding = ({ setPage, isMobile }) => {
  // This would come from the API in a real app, using the quiz slice of the store
  const dispatch = useDispatch()

  const { quizHistory, openSessions } = useSelector((state) => state.quiz)
  const courseDetails = useSelector((state) => state.course?.courseDetailsData);
  const courseProgress = courseDetails?.progress_percent;
  const examsUnlocked = Number(courseProgress) >= 100;
  const [startError, setStartError] = useState("");


  useEffect(() => {
    dispatch(getQuizHistory());
    dispatch(getOpenQuizSessions());
    dispatch(getCourseDetails({ slug: "cphq-exam-preparation" }));
  }, [dispatch]);

  const baseStyles = {
    title: {
      fontWeight: 700,
      fontSize: isMobile ? "28px" : "42px",
      color: "#0F141F",
      margin: "28px 0 0 0",
      textAlign: "center",
    },
    subtitle: {
      fontWeight: 400,
      textAlign: "center",
      fontSize: isMobile ? "15px" : "17px",
      color: "#0F141F",
      marginTop: "8px",
    },
    cardsRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: "28px",
      justifyContent: "center",
      margin: "38px auto 0 auto",
      padding: "30px",
      background: "#fff",
      borderRadius: "18px",
      boxShadow: "0 1px 8px #0001",
      maxWidth: "1100px",
    },
    card: {
      flex: "1 1 190px",
      maxWidth: isMobile ? "100%" : "210px",
      width: isMobile ? "100%" : "auto",
      background: "#F5F8FF",
      borderRadius: "14px",
      minHeight: "320px",
      padding: "28px 18px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    mockCard: { background: "#E8FBF7" },
    cardIcon: {
      width: "68px",
      height: "68px",
      borderRadius: "50%",
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "18px",
      flexShrink: 0,
    },
    cardTitle: {
      fontWeight: 700,
      fontSize: "20px",
      color: "#18314F",
      marginBottom: "8px",
      textAlign: "center",
      lineHeight: 1.25,
      minHeight: "50px",
    },
    cardDesc: {
      fontSize: "15px",
      color: "#6B7A90",
      marginBottom: "24px",
      textAlign: "center",
      flex: "1 1 auto",
    },
    cardBtn: {
      background: "#18314F",
      color: "#fff",
      fontWeight: 600,
      border: "none",
      borderRadius: "6px",
      padding: "10px 12px",
      fontSize: "16px",
      cursor: "pointer",
      marginTop: "auto",
      width: "100%",
      minHeight: "46px",
    },
    lockedBtn: {
      background: "#98A2B3",
      cursor: "not-allowed",
    },
    mockBtn: { background: "#17D1A3" },
    historySection: {
      background: "#fff",
      borderRadius: "14px",
      marginTop: "38px",
      boxShadow: "0 1px 8px #0001",
      overflow: "hidden",
    },
    historyTitle: {
      fontWeight: 700,
      fontSize: isMobile ? "18px" : "21px",
      color: "#18314F",
      padding: isMobile ? "20px 16px 10px 16px" : "28px 32px 12px 32px",
    },
    historyTableWrap: {
      width: "100%",
      overflowX: "auto",
    },
    historyTable: {
      width: "100%",
      minWidth: "760px",
      borderCollapse: "collapse",
    },
    historyTh: {
      padding: "12px 32px",
      textAlign: "left",
      whiteSpace: "nowrap",
    },
    historyTd: {
      padding: "16px 32px",
      color: "#667797",
      verticalAlign: "top",
    },
    historyCardList: {
      padding: "0 12px 12px",
    },
    historyCard: {
      padding: "14px",
      border: "1px solid #E3E8F7",
      borderRadius: "10px",
      background: "#FAFCFF",
      marginTop: "12px",
    },
    historyCardRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: "12px",
      marginBottom: "10px",
      flexWrap: "wrap",
    },
    historyCardTitle: {
      fontWeight: 700,
      color: "#18314F",
      minWidth: 0,
      overflowWrap: "anywhere",
    },
    historyCardMeta: {
      fontSize: "13px",
      color: "#667797",
      lineHeight: 1.45,
      overflowWrap: "anywhere",
    },
    historyActionBtn: {
      background: "#fff",
      border: "1px solid #0D355C",
      color: "#0D355C",
      fontWeight: 600,
      borderRadius: "6px",
      padding: "7px 18px",
      cursor: "pointer",
      width: "100%",
      marginTop: "10px",
    },
  };

  const mobileStyles = {
    cardsRow: { flexDirection: "column", padding: "20px", gap: "20px" },
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const parsedDate = new Date(value);

    if (Number.isNaN(parsedDate.getTime())) return value;

    return parsedDate.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (item) => {
    const duration =
      item.duration_minutes ??
      item.duration ??
      item.time_taken;

    if (duration === undefined || duration === null || duration === "") return "-";
    if (typeof duration === "number") return `${duration} min`;

    return duration;
  };

  const formatScore = (item) => {
    const scorePercent = item.score_percent ?? item.percentage ?? item.percent;

    if (scorePercent !== undefined && scorePercent !== null) return `${scorePercent}%`;
    if (item.score !== undefined && item.total_questions) {
      return `${item.score}/${item.total_questions}`;
    }

    return "-";
  };

  // Only real question-bank sessions (quiz/mock/practice/final exam) store a
  // per-question breakdown (QuizAttempt rows) that "Review" can show. Chapter
  // quizzes and module assessments only ever persist the aggregate score, so
  // there's nothing to review — the id used to be sent to /quiz/<id>/result/
  // anyway, which doesn't understand those non-numeric ids and returned
  // Django's raw HTML 404 page, which rendered as if it were quiz content.
  const REVIEWABLE_SESSION_TYPES = ['quiz', 'mock', 'pre_assessment', 'practice_test', 'final_exam'];
  const isReviewable = (item) => REVIEWABLE_SESSION_TYPES.includes(item.session_type) || Boolean(item.has_review);

  const historyItems = (Array.isArray(quizHistory) ? quizHistory : quizHistory?.results || []).map((item) => {
    const rawStatus = typeof item.status === "object" ? item.status?.text : item.status;
    const statusLabel =
      item.passed === true
        ? "Passed"
        : item.passed === false
          ? "Failed"
          : rawStatus || "-";
    const scoreText = formatScore(item);

    return {
      ...item,
      displayTitle: item.topic_name || item.title || item.session_type || "Quiz",
      displayDuration: formatDuration(item),
      displayDate: formatDateTime(item.completed_at || item.date || item.created_at || item.started_at),
      displayScore: scoreText,
      status: {
        color: item.status?.color || (item.passed ? "#177A38" : "#B42318"),
        text: statusLabel,
      },
      statusText: `${scoreText} (${statusLabel})`,
      isReviewable: isReviewable(item),
    };
  });

  const practiceStatus = bestExamStatus(
    quizHistory,
    (item) => item.session_type === "practice_test"
  );
  const preStatus = bestExamStatus(
    quizHistory,
    (item) => item.session_type === "pre_assessment"
  );
  const quizStatus = bestExamStatus(
    quizHistory,
    (item) => item.session_type === "quiz"
  );
  const openPre = findOpenSession(openSessions, "pre_assessment");
  const openQuiz = findOpenSession(openSessions, "quiz");
  const openPractice = findOpenSession(openSessions, "practice_test");

  const openExam = async (result, failureMessage, defaultQuestionNumber = 1) => {
    const session = result.payload;
    const sessionId = session?.session_id || session?.id;
    const questionNumber = session?.resume_question_number || defaultQuestionNumber;
    if (result.meta?.requestStatus === "fulfilled" && sessionId) {
      dispatch(
        getQuizQuestions({
          session_id: sessionId,
          question_number: questionNumber,
        })
      );
      setPage("quiz");
      return;
    }
    setStartError(session?.message || session?.error || session?.detail || failureMessage);
  };

  const handleStartExam = async (sessionType, numQuestions, failureMessage, extra = {}) => {
    setStartError("");
    const result = await dispatch(
      startMocKExamSession({
        session_type: sessionType,
        num_questions: numQuestions,
        ...extra,
      })
    );
    await openExam(result, failureMessage);
  };

  const handleResumeExam = async (openSession, failureMessage) => {
    setStartError("");
    const result = await dispatch(resumeQuizSession(openSession.session_id));
    await openExam(result, failureMessage, openSession.resume_question_number);
  };

  const handleReview = (item) => {
    if (!isReviewable(item)) return;
    if (item.review_url) {
      dispatch(getChapterReview({ reviewUrl: item.review_url }));
      setPage("quiz");
      return;
    }
    const sessionId = item.session_id || item.id;

    if (sessionId) {
      dispatch(getSessionResult({ session_id: sessionId }));
      setPage("quiz");
    }
  };

  const HistoryTable = () => (
    <div style={baseStyles.historyTableWrap}>
      <table style={baseStyles.historyTable}>
        <thead>
          <tr style={{ background: "#fff", color: "#6B7A90" }}>
            <th style={baseStyles.historyTh}>Topic</th>
            <th style={baseStyles.historyTh}>Duration</th>
            <th style={baseStyles.historyTh}>Completed</th>
            <th style={baseStyles.historyTh}>Score</th>
            <th style={baseStyles.historyTh}>Status</th>
            <th style={baseStyles.historyTh}>Action</th>
          </tr>
        </thead>
        <tbody>
          {historyItems.map((item, i) => (
            <tr key={item.id || item.session_id || i} style={{ borderTop: "1px solid #F0F1F3" }}>
              <td style={baseStyles.historyTd}>
                {item.displayTitle}
              </td>
              <td style={baseStyles.historyTd}>
                {item.displayDuration}
              </td>
              <td style={baseStyles.historyTd}>
                {item.displayDate}
              </td>
              <td style={{ ...baseStyles.historyTd, fontWeight: 600 }}>
                {item.displayScore}
              </td>
              <td style={{ ...baseStyles.historyTd, color: item.status.color, fontWeight: 600 }}>
                {item.status.text}
              </td>
              <td style={baseStyles.historyTd}>
                {item.isReviewable ? (
                  <button style={baseStyles.historyActionBtn} onClick={() => handleReview(item)}>Review</button>
                ) : (
                  <span style={{ color: "#B7C0CE" }}>—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const HistoryCardList = () => (
    <div style={baseStyles.historyCardList}>
      {historyItems.map((item, i) => (
        <div key={item.id || item.session_id || i} style={baseStyles.historyCard}>
          <div style={baseStyles.historyCardRow}>
            <span style={baseStyles.historyCardTitle}>
              {item.displayTitle}
            </span>
            <span style={{ fontWeight: 700, color: "#0F141F" }}>
              {item.displayScore}
            </span>
          </div>
          <div style={{ ...baseStyles.historyCardRow, marginBottom: 0 }}>
            <span style={baseStyles.historyCardMeta}>
              Duration: {item.displayDuration}
              <br />
              Completed: {item.displayDate}
            </span>
            <span style={{ fontSize: "13px", color: item.status.color, fontWeight: 700 }}>
              {item.status.text}
            </span>
          </div>
          {item.isReviewable && (
            <button style={baseStyles.historyActionBtn} onClick={() => handleReview(item)}>Review</button>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <>
      <h1 style={baseStyles.title}>Question Bank</h1>
      <div style={baseStyles.subtitle}>
        Explore our collection of educational resources for comprehensive CPHQ
        preparation.
      </div>
      {startError && (
        <div
          style={{
            maxWidth: "620px",
            margin: "16px auto 0",
            padding: "12px 16px",
            borderRadius: "8px",
            background: "#FEF2F2",
            border: "1px solid #FECACA",
            color: "#B42318",
            fontSize: "14px",
            fontWeight: 500,
            textAlign: "center",
          }}
        >
          {startError}
        </div>
      )}
      <div
        style={{
          ...baseStyles.cardsRow,
          ...(isMobile && mobileStyles.cardsRow),
        }}
      >
        <div style={baseStyles.card}>
          <div style={baseStyles.cardIcon}>
            <svg width="68" height="68" viewBox="0 0 68 68" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="68" height="68" rx="34" fill="#5E7EB7" />
              <path d="M24 22h14l6 6v18a2 2 0 0 1-2 2H24a2 2 0 0 1-2-2V24a2 2 0 0 1 2-2Z" stroke="white" strokeWidth="1.8" />
              <path d="M38 22v6h6M28 34h12M28 39h8" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div style={baseStyles.cardTitle}>Pre-Assessment</div>
          <div style={baseStyles.cardDesc}>
            A short check before you start studying
          </div>
          <div style={{ ...baseStyles.cardDesc, color: statusColor(preStatus.tone), fontWeight: 700 }}>
            {preStatus.label}
          </div>
          {openPre ? (
            <div style={dualActionRow}>
              <button style={baseStyles.cardBtn} onClick={() => handleResumeExam(openPre, "Unable to resume pre-assessment.")}>
                Resume
              </button>
              <button
                style={{ ...baseStyles.cardBtn, ...secondaryBtn }}
                onClick={() => handleStartExam("pre_assessment", 25, "Unable to start pre-assessment.", { force_new: true })}
              >
                Start new
              </button>
            </div>
          ) : (
            <button style={baseStyles.cardBtn} onClick={() => handleStartExam("pre_assessment", 25, "Unable to start pre-assessment.")}>
              {preStatus.label === "Not Attempted" ? "Start Pre-Assessment" : "Retake"}
            </button>
          )}
        </div>
        <div style={baseStyles.card}>
          <div style={baseStyles.cardIcon}>
            <svg
              width="69"
              height="68"
              viewBox="0 0 69 68"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="0.75" width="68" height="68" rx="34" fill="#0D355C" />
              <path
                d="M28.0839 36.2501C27.5019 36.251 26.927 36.3828 26.3995 36.6365C25.872 36.8901 25.4046 37.2594 25.0297 37.7186C24.6549 38.1777 24.3818 38.7157 24.2294 39.295C24.077 39.8743 24.049 40.4809 24.1474 41.0724C24.2458 41.664 24.4682 42.2263 24.7991 42.7201C25.13 43.2139 25.5613 43.6272 26.0631 43.9314C26.5649 44.2355 27.125 44.423 27.7043 44.4809C28.2836 44.5387 28.8682 44.4655 29.4172 44.2663"
                stroke="white"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M24.4359 38.4569C23.638 38.0607 22.9482 37.4659 22.429 36.7261C21.9098 35.9863 21.5773 35.1249 21.4617 34.2194C21.346 33.314 21.4509 32.3931 21.7667 31.5399C22.0824 30.6867 22.5993 29.9281 23.2705 29.3324M23.3105 29.221C22.8733 28.5462 22.6816 27.7336 22.7695 26.9271C22.8574 26.1206 23.2192 25.3722 23.7908 24.8143C24.3624 24.2565 25.107 23.9252 25.8927 23.8791C26.6784 23.833 27.4546 24.0751 28.0839 24.5625M28.4012 24.6519C28.1885 24.1858 28.0801 23.6764 28.084 23.1613C28.0879 22.6462 28.2039 22.1386 28.4236 21.676C28.6434 21.2134 28.9611 20.8077 29.3533 20.4887C29.7455 20.1698 30.2022 19.9458 30.6897 19.8334C31.1771 19.721 31.6829 19.723 32.1695 19.8392C32.6561 19.9555 33.1111 20.183 33.501 20.505C33.8908 20.827 34.2055 21.2353 34.4218 21.6996C34.6381 22.1639 34.7505 22.6724 34.7505 23.1875V44.5C34.7505 45.2293 34.4696 45.9288 33.9695 46.4445C33.4694 46.9603 32.7911 47.25 32.0839 47.25C31.3766 47.25 30.6983 46.9603 30.1983 46.4445C29.6982 45.9288 29.4172 45.2293 29.4172 44.5M34.7505 26.625C34.7505 27.719 35.172 28.7682 35.9221 29.5418C36.6723 30.3154 37.6897 30.75 38.7505 30.75M41.4172 36.25C41.9992 36.2509 42.574 36.3827 43.1015 36.6364C43.629 36.89 44.0965 37.2593 44.4713 37.7185C44.8462 38.1777 45.1193 38.7157 45.2717 39.2949C45.4241 39.8742 45.4521 40.4808 45.3537 41.0723C45.2553 41.6639 45.0329 42.2262 44.702 42.72C44.3711 43.2138 43.9398 43.6272 43.438 43.9313C42.9362 44.2354 42.3761 44.4229 41.7968 44.4808C41.2175 44.5386 40.6329 44.4654 40.0839 44.2662"
                stroke="white"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M45.0647 38.4569C45.8626 38.0607 46.5523 37.4659 47.0715 36.7261C47.5908 35.9863 47.9232 35.1249 48.0389 34.2194C48.1545 33.314 48.0497 32.3931 47.7339 31.5399C47.4181 30.6867 46.9012 29.9281 46.23 29.3324M46.19 29.221C46.6273 28.5462 46.8189 27.7336 46.731 26.9271C46.6431 26.1206 46.2814 25.3722 45.7098 24.8143C45.1382 24.2565 44.3936 23.9252 43.6079 23.8791C42.8221 23.833 42.0459 24.0751 41.4167 24.5625M34.75 23.1875C34.7501 22.6724 34.8624 22.1639 35.0787 21.6996C35.295 21.2353 35.6097 20.827 35.9996 20.505C36.3894 20.183 36.8445 19.9555 37.3311 19.8392C37.8177 19.723 38.3234 19.721 38.8108 19.8334C39.2983 19.9458 39.755 20.1698 40.1472 20.4887C40.5395 20.8077 40.8572 21.2134 41.0769 21.676C41.2966 22.1386 41.4127 22.6462 41.4166 23.1613C41.4204 23.6764 41.312 24.1858 41.0993 24.6519M40.0833 44.5C40.0833 45.2293 39.8024 45.9288 39.3023 46.4445C38.8022 46.9603 38.1239 47.25 37.4167 47.25C36.7094 47.25 36.0311 46.9603 35.531 46.4445C35.031 45.9288 34.75 45.2293 34.75 44.5"
                stroke="white"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div style={baseStyles.cardTitle}>Quizzes</div>
          <div style={baseStyles.cardDesc}>
            Short quizzes to check your knowledge
          </div>
          <div style={{ ...baseStyles.cardDesc, color: statusColor(quizStatus.tone), fontWeight: 700 }}>
            {quizStatus.label}
          </div>
          {openQuiz ? (
            <div style={dualActionRow}>
              <button style={baseStyles.cardBtn} onClick={() => handleResumeExam(openQuiz, "Unable to resume quiz.")}>
                Resume
              </button>
              <button
                style={{ ...baseStyles.cardBtn, ...secondaryBtn }}
                onClick={() => handleStartExam("quiz", 25, "Unable to start quiz.", { force_new: true })}
              >
                Start new
              </button>
            </div>
          ) : (
            <button
              style={baseStyles.cardBtn}
              onClick={() => handleStartExam("quiz", 25, "Unable to start quiz.")}
            >
              {quizStatus.label === "Not Attempted" ? "Start Quiz" : "Retake"}
            </button>
          )}
        </div>
        <div style={{ ...baseStyles.card, ...baseStyles.mockCard }}>
          <div style={baseStyles.cardIcon}>
            <svg
              width="69"
              height="68"
              viewBox="0 0 69 68"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="0.25" width="68" height="68" rx="34" fill="#27CEA7" />
              <path
                d="M45.8763 21.8737L49.3331 22.5646C49.5187 22.6018 49.6713 22.7379 49.7249 22.9214C49.752 23.0106 49.7544 23.1054 49.7319 23.1959C49.7094 23.2863 49.6628 23.3689 49.5971 23.435L46.7652 26.2689C46.4259 26.6063 45.9671 26.7961 45.4886 26.7969H43.1394L37.2406 32.6977C37.3532 33.1226 37.3739 33.5666 37.3013 34.0002C37.2288 34.4337 37.0647 34.8468 36.82 35.212C36.5753 35.5772 36.2556 35.886 35.8822 36.1179C35.5088 36.3498 35.0902 36.4995 34.6544 36.5569C34.2186 36.6144 33.7756 36.5783 33.3548 36.4512C32.9341 36.324 32.5452 36.1086 32.2142 35.8193C31.8832 35.5301 31.6177 35.1736 31.4352 34.7737C31.2528 34.3738 31.1577 33.9396 31.1563 33.5C31.1561 33.0253 31.2653 32.5569 31.4753 32.1311C31.6852 31.7053 31.9904 31.3335 32.367 31.0446C32.7437 30.7557 33.1818 30.5573 33.6475 30.4648C34.1131 30.3724 34.5938 30.3883 35.0523 30.5114L40.9531 24.6086V22.2635C40.9531 21.785 41.1429 21.3251 41.4811 20.9868L44.315 18.1529C44.3811 18.0872 44.4637 18.0406 44.5541 18.0181C44.6446 17.9956 44.7394 17.998 44.8286 18.0251C45.0121 18.0787 45.1482 18.2313 45.1854 18.4169L45.8763 21.8737Z"
                fill="white"
              />
              <path
                d="M21.8742 33.5C21.8763 35.2696 22.2579 37.0181 22.9932 38.6276C23.7286 40.2371 24.8005 41.6702 26.1368 42.8302C27.4731 43.9903 29.0426 44.8502 30.7395 45.3521C32.4364 45.8539 34.2212 45.986 35.9735 45.7395C37.7258 45.4929 39.4048 44.8734 40.8973 43.9228C42.3898 42.9721 43.6611 41.7125 44.6254 40.2288C45.5897 38.745 46.2245 37.0718 46.4871 35.3218C46.7498 33.5719 46.634 31.7859 46.1478 30.0845C46.0795 29.8857 46.0524 29.6751 46.0681 29.4656C46.0838 29.256 46.142 29.0518 46.2392 28.8654C46.3363 28.679 46.4704 28.5144 46.6332 28.3815C46.796 28.2485 46.9842 28.1502 47.1862 28.0923C47.3883 28.0344 47.6 28.0183 47.8085 28.0448C48.017 28.0714 48.2179 28.1401 48.399 28.2468C48.58 28.3535 48.7375 28.4959 48.8618 28.6654C48.9861 28.8349 49.0746 29.0279 49.1219 29.2327C50.0635 32.5276 49.8846 36.0419 48.6134 39.2241C47.3421 42.4064 45.0505 45.0767 42.0979 46.8159C39.1453 48.5552 35.6988 49.2651 32.2992 48.8343C28.8996 48.4034 25.7392 46.8562 23.3138 44.4354C20.8911 42.0106 19.3422 38.8499 18.9104 35.4495C18.4786 32.0491 19.1882 28.6016 20.928 25.6482C22.6677 22.6948 25.339 20.4029 28.5225 19.1321C31.7059 17.8614 35.2212 17.6838 38.5165 18.6273C38.9089 18.742 39.2399 19.0074 39.4372 19.3654C39.6345 19.7234 39.6821 20.145 39.5696 20.538C39.457 20.931 39.1935 21.2634 38.8365 21.4627C38.4796 21.662 38.0583 21.7119 37.6647 21.6015C35.822 21.0723 33.8816 20.9785 31.9964 21.3276C30.1113 21.6766 28.333 22.4588 26.8019 23.6125C25.2708 24.7663 24.0286 26.26 23.1735 27.9759C22.3183 29.6918 21.8735 31.5829 21.8742 33.5Z"
                fill="white"
              />
              <path
                d="M28.0615 33.5C28.0617 34.6273 28.3696 35.7332 28.952 36.6984C29.5345 37.6636 30.3695 38.4515 31.3668 38.977C32.3642 39.5025 33.4861 39.7458 34.6116 39.6805C35.737 39.6153 36.8233 39.244 37.7532 38.6067C38.683 37.9678 39.4204 37.0869 39.8857 36.0592C40.351 35.0316 40.5265 33.8962 40.3932 32.776C40.3572 32.5042 40.3942 32.2277 40.5006 31.9749C40.6069 31.7221 40.7787 31.5023 40.9983 31.338C41.2178 31.1737 41.4772 31.0709 41.7497 31.0402C42.0222 31.0094 42.2979 31.0519 42.5485 31.1631C42.7991 31.273 43.0161 31.4473 43.1773 31.6683C43.3386 31.8893 43.4383 32.1491 43.4663 32.4213C43.6905 34.3355 43.3131 36.272 42.3867 37.962C41.4602 39.652 40.0306 41.0117 38.2964 41.8523C36.5621 42.693 34.6091 42.9729 32.7085 42.6532C30.808 42.3335 29.054 41.4301 27.6902 40.0683C26.3264 38.7066 25.4203 36.954 25.0977 35.0539C24.7752 33.1538 25.0521 31.2004 25.8901 29.4649C26.7281 27.7293 28.0856 26.2977 29.7742 25.3687C31.4628 24.4397 33.3987 24.0594 35.3133 24.2806C35.5183 24.2996 35.7174 24.3594 35.899 24.4563C36.0806 24.5533 36.2411 24.6856 36.3709 24.8454C36.5007 25.0052 36.5974 25.1892 36.6551 25.3869C36.7129 25.5845 36.7306 25.7916 36.7073 25.9962C36.6839 26.2007 36.62 26.3986 36.5192 26.5781C36.4184 26.7576 36.2828 26.9152 36.1203 27.0417C35.9578 27.1681 35.7717 27.2608 35.573 27.3144C35.3742 27.368 35.1667 27.3814 34.9627 27.3537C34.0957 27.253 33.2173 27.3369 32.385 27.5999C31.5528 27.8628 30.7856 28.2988 30.1338 28.8793C29.4821 29.4598 28.9605 30.1715 28.6033 30.9679C28.2462 31.7643 28.0615 32.6272 28.0615 33.5Z"
                fill="white"
              />
            </svg>
          </div>
          <div style={baseStyles.cardTitle}>Mock Exam</div>
          <div style={baseStyles.cardDesc}>
            {examsUnlocked
              ? "Full mock exams for the course"
              : "Opens after you reach 100% course progress"}
          </div>
          <button
            style={{
              ...baseStyles.cardBtn,
              ...baseStyles.mockBtn,
              ...(!examsUnlocked ? baseStyles.lockedBtn : {}),
            }}
            disabled={!examsUnlocked}
            onClick={() => {
              if (!examsUnlocked) return;
              setPage("mock-exams");
            }}
          >
            {examsUnlocked ? "View Mock Exams" : "Locked until 100%"}
          </button>
        </div>
        <div style={baseStyles.card}>
          <div style={baseStyles.cardIcon}>
            <svg width="68" height="68" viewBox="0 0 68 68" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="68" height="68" rx="34" fill="#0D355C" />
              <path d="M26 40.5 24.5 45.5 29.5 44 43.2 30.3a2.1 2.1 0 0 0 0-3L40.7 24.8a2.1 2.1 0 0 0-3 0L26 40.5Z" stroke="white" strokeWidth="1.8" strokeLinejoin="round" />
              <path d="M36.2 26.3 41.7 31.8" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div style={baseStyles.cardTitle}>Practice Test</div>
          <div style={baseStyles.cardDesc}>
            {examsUnlocked
              ? "A full practice test for the course"
              : "Opens after you reach 100% course progress"}
          </div>
          {examsUnlocked && (
            <div style={{ ...baseStyles.cardDesc, color: statusColor(practiceStatus.tone), fontWeight: 700 }}>
              {practiceStatus.label}
            </div>
          )}
          {examsUnlocked && openPractice ? (
            <div style={dualActionRow}>
              <button style={baseStyles.cardBtn} onClick={() => handleResumeExam(openPractice, "Unable to resume practice test.")}>
                Resume
              </button>
              <button
                style={{ ...baseStyles.cardBtn, ...secondaryBtn }}
                onClick={() => handleStartExam("practice_test", 300, "Unable to start practice test.", { force_new: true })}
              >
                Start new
              </button>
            </div>
          ) : (
            <button
              style={{ ...baseStyles.cardBtn, ...(!examsUnlocked ? baseStyles.lockedBtn : {}) }}
              disabled={!examsUnlocked}
              onClick={() => {
                if (!examsUnlocked) return;
                handleStartExam("practice_test", 300, "Unable to start practice test.");
              }}
            >
              {examsUnlocked
                ? (practiceStatus.label === "Not Attempted" ? "Start Practice Test" : "Retake")
                : "Locked until 100%"}
            </button>
          )}
        </div>
        <div style={{ ...baseStyles.card, background: "#F4F0FF" }}>
          <div style={baseStyles.cardIcon}>
            <svg width="68" height="68" viewBox="0 0 68 68" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="68" height="68" rx="34" fill="#6941C6" />
              <circle cx="34" cy="35" r="10" stroke="white" strokeWidth="1.8" />
              <path d="M34 29v6l4 2M31 22h6" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div style={baseStyles.cardTitle}>Final Exam</div>
          <div style={baseStyles.cardDesc}>
            {examsUnlocked
              ? "Timed final exams for the course"
              : "Opens after you reach 100% course progress"}
          </div>
          <button
            style={{
              ...baseStyles.cardBtn,
              background: examsUnlocked ? "#6941C6" : "#98A2B3",
              cursor: examsUnlocked ? "pointer" : "not-allowed",
            }}
            disabled={!examsUnlocked}
            onClick={() => {
              if (!examsUnlocked) return;
              setPage("final-exams");
            }}
          >
            {examsUnlocked ? "View Final Exams" : "Locked until 100%"}
          </button>
        </div>
      </div>
      <div style={baseStyles.historySection}>
        <div style={baseStyles.historyTitle}>History</div>
        {isMobile ? <HistoryCardList /> : <HistoryTable />}
      </div>
    </>
  );
};


// ====================================================================
// SUB-COMPONENT: Exam set hubs (module mocks / practice / final)
// ====================================================================
const examHubStyles = (isMobile) => ({
  title: {
    fontWeight: 700,
    fontSize: isMobile ? "26px" : "34px",
    color: "#0F141F",
    margin: "20px 0 8px",
  },
  subtitle: {
    color: "#5A6A85",
    fontSize: isMobile ? "14px" : "16px",
    marginBottom: "22px",
    lineHeight: 1.5,
  },
  list: {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
    gap: "12px",
  },
  item: {
    background: "#fff",
    border: "1px solid #E3EAF5",
    borderRadius: "12px",
    padding: isMobile ? "14px" : "16px 18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    boxShadow: "0 4px 14px rgba(13, 53, 92, 0.05)",
  },
  itemTitle: {
    fontWeight: 700,
    color: "#0F141F",
    fontSize: isMobile ? "14px" : "15px",
    marginBottom: "4px",
  },
  itemMeta: {
    color: "#6B7C93",
    fontSize: "12px",
  },
  btn: {
    border: 0,
    borderRadius: "999px",
    background: "#17D1A3",
    color: "#fff",
    fontWeight: 700,
    fontSize: "13px",
    padding: "10px 14px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  backBtn: {
    border: "1px solid #C5D0E0",
    background: "#fff",
    color: "#0D355C",
    borderRadius: "999px",
    fontWeight: 600,
    fontSize: "13px",
    padding: "8px 14px",
    cursor: "pointer",
    marginBottom: "8px",
  },
  error: {
    color: "#B42318",
    background: "#FEF3F2",
    border: "1px solid #FDA29B",
    borderRadius: "10px",
    padding: "10px 12px",
    marginTop: "16px",
    fontSize: "13px",
  },
});

const useStartExamSession = (setPage) => {
  const dispatch = useDispatch();
  const [startError, setStartError] = useState("");
  const [startingKey, setStartingKey] = useState("");

  const openFromResult = async (result, key, failureMessage, questionNumber = 1) => {
    const session = result.payload;
    const sessionId = session?.session_id || session?.id;
    setStartingKey("");
    if (result.meta?.requestStatus === "fulfilled" && sessionId) {
      dispatch(getQuizQuestions({
        session_id: sessionId,
        question_number: session?.resume_question_number || questionNumber,
      }));
      setPage("quiz");
      return;
    }
    setStartError(session?.message || session?.error || session?.detail || failureMessage);
  };

  const startExam = async (payload, key, failureMessage) => {
    setStartError("");
    setStartingKey(key);
    const result = await dispatch(startMocKExamSession(payload));
    await openFromResult(result, key, failureMessage);
  };

  const resumeExam = async (openSession, key, failureMessage) => {
    setStartError("");
    setStartingKey(key);
    const result = await dispatch(resumeQuizSession(openSession.session_id));
    await openFromResult(result, key, failureMessage, openSession.resume_question_number);
  };

  return { startExam, resumeExam, startError, startingKey };
};

const MockExamsHub = ({ setPage, isMobile }) => {
  const dispatch = useDispatch();
  const courseDetails = useSelector((state) => state.course?.courseDetailsData);
  const quizHistory = useSelector((state) => state.quiz?.quizHistory);
  const { startExam, resumeExam, startError, startingKey } = useStartExamSession(setPage);
  const openSessions = useSelector((state) => state.quiz?.openSessions);
  const styles = examHubStyles(isMobile);

  const modules = [...(courseDetails?.domains || [])]
    .filter((domain) => Number(domain.order) >= 1 && Number(domain.order) <= 7)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  useEffect(() => {
    if (!courseDetails) {
      dispatch(getCourseDetails({ slug: "cphq-exam-preparation" }));
    }
    dispatch(getQuizHistory());
    dispatch(getOpenQuizSessions());
  }, [courseDetails, dispatch]);

  return (
    <div>
      <button type="button" style={styles.backBtn} onClick={() => setPage("bank")}>
        ← Back to Question Bank
      </button>
      <h1 style={styles.title}>Mock Exams</h1>
      <p style={styles.subtitle}>
        Choose a module and start its mock exam.
      </p>

      <div style={styles.list}>
        {modules.map((domain) => {
          const key = `module-${domain.id}`;
          const status = bestExamStatus(
            quizHistory,
            (item) => item.session_type === "mock" && Number(item.domain_order) === Number(domain.order)
          );
          const openMock = findOpenSession(openSessions, "mock", { domainId: domain.id });
          return (
            <div key={domain.id} style={styles.item}>
              <div>
                <div style={styles.itemTitle}>
                  Module {domain.order}: {domain.title?.replace(/^Module\s+\d+:\s*/i, "") || domain.title}
                </div>
                <div style={{ ...styles.itemMeta, color: statusColor(status.tone), fontWeight: 700 }}>
                  {openMock ? "In progress" : status.label}
                </div>
              </div>
              {openMock ? (
                <div style={dualActionRow}>
                  <button
                    type="button"
                    style={styles.btn}
                    disabled={Boolean(startingKey)}
                    onClick={() => resumeExam(openMock, key, "Unable to resume module mock.")}
                  >
                    {startingKey === key ? "Opening…" : "Resume"}
                  </button>
                  <button
                    type="button"
                    style={{ ...styles.btn, ...secondaryBtn }}
                    disabled={Boolean(startingKey)}
                    onClick={() =>
                      startExam(
                        { session_type: "mock", domain_id: domain.id, num_questions: 300, force_new: true },
                        key,
                        "Unable to start module mock."
                      )
                    }
                  >
                    Start new
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  style={styles.btn}
                  disabled={Boolean(startingKey)}
                  onClick={() =>
                    startExam(
                      { session_type: "mock", domain_id: domain.id, num_questions: 300 },
                      key,
                      "Unable to start module mock."
                    )
                  }
                >
                  {startingKey === key ? "Starting…" : (status.label === "Not Attempted" ? "Start" : "Retake")}
                </button>
              )}
            </div>
          );
        })}
        {!modules.length && <div style={styles.itemMeta}>Loading modules…</div>}
      </div>

      {startError && <div style={styles.error}>{startError}</div>}
    </div>
  );
};

const FinalExamsHub = ({ setPage, isMobile }) => {
  const dispatch = useDispatch();
  const quizHistory = useSelector((state) => state.quiz?.quizHistory);
  const { startExam, resumeExam, startError, startingKey } = useStartExamSession(setPage);
  const openSessions = useSelector((state) => state.quiz?.openSessions);
  const styles = examHubStyles(isMobile);

  useEffect(() => {
    dispatch(getQuizHistory());
    dispatch(getOpenQuizSessions());
  }, [dispatch]);
  const exams = [
    { bank_set: 1, title: "Final Exam 1" },
    { bank_set: 2, title: "Final Exam 2" },
  ];

  return (
    <div>
      <button type="button" style={styles.backBtn} onClick={() => setPage("bank")}>
        ← Back to Question Bank
      </button>
      <h1 style={styles.title}>Final Exams</h1>
      <p style={styles.subtitle}>
        Choose a final exam to begin.
      </p>
      <div style={styles.list}>
        {exams.map((item) => {
          const key = `final-${item.bank_set}`;
          const status = bestExamStatus(
            quizHistory,
            (entry) => entry.session_type === "final_exam" && Number(entry.mock_exam_id) === item.bank_set
          );
          const openFinal = findOpenSession(openSessions, "final_exam", { bankSet: item.bank_set });
          return (
            <div key={item.bank_set} style={styles.item}>
              <div>
                <div style={styles.itemTitle}>{item.title}</div>
                <div style={{ ...styles.itemMeta, color: statusColor(status.tone), fontWeight: 700 }}>
                  {openFinal ? "In progress" : status.label}
                </div>
              </div>
              {openFinal ? (
                <div style={dualActionRow}>
                  <button
                    type="button"
                    style={{ ...styles.btn, background: "#6941C6" }}
                    disabled={Boolean(startingKey)}
                    onClick={() => resumeExam(openFinal, key, "Unable to resume final exam.")}
                  >
                    {startingKey === key ? "Opening…" : "Resume"}
                  </button>
                  <button
                    type="button"
                    style={{ ...styles.btn, ...secondaryBtn }}
                    disabled={Boolean(startingKey)}
                    onClick={() =>
                      startExam(
                        { session_type: "final_exam", bank_set: item.bank_set, num_questions: 300, force_new: true },
                        key,
                        "Unable to start final exam."
                      )
                    }
                  >
                    Start new
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  style={{ ...styles.btn, background: "#6941C6" }}
                  disabled={Boolean(startingKey)}
                  onClick={() =>
                    startExam(
                      { session_type: "final_exam", bank_set: item.bank_set, num_questions: 300 },
                      key,
                      "Unable to start final exam."
                    )
                  }
                >
                  {startingKey === key ? "Starting…" : (status.label === "Not Attempted" ? "Start" : "Retake")}
                </button>
              )}
            </div>
          );
        })}
      </div>
      {startError && <div style={styles.error}>{startError}</div>}
    </div>
  );
};

const QuizView = ({ setPage, isMobile }) => {
  const [selected, setSelected] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(1);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const dispatch = useDispatch();
  const { quiz, quizQuestions, loading, error, submittedAnswer, sessionResult } = useSelector((state) => state.quiz);
  const sessionId = quiz?.session_id || quiz?.id;
  const mockTimeMinutes = quiz?.time_limit_minutes;
  const mockTimeSeconds = quiz?.time_remaining_seconds;
  const shouldShowMockTime =
    (mockTimeMinutes != null && Number(mockTimeMinutes) > 0) ||
    (mockTimeSeconds != null && Number(mockTimeSeconds) > 0);
  const numericMockSeconds = Number(mockTimeSeconds);
  const numericMockMinutes = Number(mockTimeMinutes);
  const initialMockSeconds =
    Number.isFinite(numericMockSeconds) && numericMockSeconds >= 0
      ? numericMockSeconds
      : Number.isFinite(numericMockMinutes) && numericMockMinutes >= 0
        ? numericMockMinutes * 60
        : 0;

  const formatClockNumber = (value) => String(value).padStart(2, "0");
  const timerHours = Math.floor(timeRemaining / 3600);
  const timerMinutes = Math.floor((timeRemaining % 3600) / 60);
  const timerSeconds = timeRemaining % 60;
  const timerParts = [
    { label: "Hours", value: formatClockNumber(timerHours) },
    { label: "Minutes", value: formatClockNumber(timerMinutes) },
    { label: "Seconds", value: formatClockNumber(timerSeconds) },
  ];

  useEffect(() => {
    if (!shouldShowMockTime) {
      setTimeRemaining(0);
      return;
    }

    setTimeRemaining(initialMockSeconds);
  }, [sessionId, initialMockSeconds, shouldShowMockTime]);

  useEffect(() => {
    if (!shouldShowMockTime || sessionResult) return undefined;

    const timer = setInterval(() => {
      setTimeRemaining((previousTime) => {
        if (previousTime <= 0) return 0;

        const nextTime = previousTime - 1;
        dispatch(setMockExamTimeRemaining(nextTime));
        return nextTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [dispatch, sessionId, shouldShowMockTime, sessionResult]);

  useEffect(() => {
    setSelected(null);
    dispatch(clearQuizError());
  }, [dispatch, quizQuestions?.question_number, quizQuestions?.question?.id]);

  useEffect(() => {
    if (quizQuestions?.question_number) {
      setQuestionNumber(quizQuestions.question_number);
    }
  }, [quizQuestions?.question_number]);

  useEffect(() => {
    if (sessionResult?.is_last_question || sessionResult?.total_questions) {
      dispatch(getQuizHistory());
    }
  }, [dispatch, sessionResult]);

  const activeQuizQuestion = quizQuestions?.question
    ? quizQuestions
    : {
      question_number: 1,
      total_questions: quiz?.total_questions,
      question: quiz?.first_question,
    };
  const questionData =
    typeof activeQuizQuestion?.question === "object" &&
      activeQuizQuestion?.question !== null
      ? activeQuizQuestion.question
      : activeQuizQuestion || {};
  const optionList =
    questionData.options ||
    questionData.choices ||
    questionData.answers ||
    questionData.answer_options ||
    activeQuizQuestion?.options ||
    activeQuizQuestion?.choices ||
    activeQuizQuestion?.answers ||
    activeQuizQuestion?.answer_options ||
    [
      questionData.option_a,
      questionData.option_b,
      questionData.option_c,
      questionData.option_d,
    ].filter(Boolean);
  const options = Array.isArray(optionList) ? optionList : Object.values(optionList || {});
  const totalQuestions =
    activeQuizQuestion?.total_questions ||
    activeQuizQuestion?.total ||
    quiz?.total_questions ||
    0;
  const currentQuestionNumber = activeQuizQuestion?.question_number || questionNumber;
  const cleanText = (value) =>
    String(value ?? "")
      .replace(/\[IMAGE:\s*[^\]]+\]/gi, "")
      .replace(/â€”/g, "-")
      .replace(/â€¢/g, "-")
      .replace(/[ \t]{2,}/g, " ")
      .trim();

  const examTitles = {
    mock: "Mock Exam",
    pre_assessment: "Pre-Assessment",
    practice_test: "Practice Test",
    final_exam: "Final Exam",
  };
  const title = quiz?.module_title
    ? `${examTitles.mock} — ${quiz.mock_label || quiz.module_title}`
    : examTitles[quiz?.session_type] ||
      questionData.topic_name ||
      quiz?.title ||
      quiz?.topic_name ||
      "Quiz";
  const questionText = cleanText(
    (typeof activeQuizQuestion?.question === "string" ? activeQuizQuestion.question : "") ||
    questionData.question ||
    questionData.question_text ||
    questionData.prompt ||
    questionData.text ||
    questionData.title ||
    ""
  );
  const questionImageUrl = questionData.image_url || activeQuizQuestion?.image_url || "";
  const explanation = submittedAnswer?.explanation || "";
  const explanationHighlight =
    submittedAnswer?.explanation_highlight ||
    submittedAnswer?.correct_explanation ||
    "";
  const correctOption = submittedAnswer?.correct_option;
  const hasAnswerResponse = Boolean(
    submittedAnswer?.recorded ||
      submittedAnswer?.is_correct !== undefined ||
      submittedAnswer?.correct_option
  );
  const showImmediateFeedback = hasAnswerResponse;
  const isLastQuestion = Boolean(submittedAnswer?.is_last_question);
  const isAlreadyAnsweredError =
    typeof error === "string" &&
    error.toLowerCase().includes("already answered");
  const canContinue = hasAnswerResponse || isAlreadyAnsweredError;
  const answerFeedbackColor =
    loading && !hasAnswerResponse
      ? "#0D355C"
      : submittedAnswer?.is_correct
        ? "#177A38"
        : "#B42318";
  const answerFeedbackBg =
    loading && !hasAnswerResponse
      ? "#FAFCFF"
      : submittedAnswer?.is_correct
        ? "#EAF8EE"
        : "#FEF3F2";
  const answerFeedbackBorder =
    loading && !hasAnswerResponse
      ? "#E3E8F7"
      : submittedAnswer?.is_correct
        ? "#B7E4C7"
        : "#FDA29B";
  const handleNext = () => {
    if (selected === null || !canContinue) return;

    if (isAlreadyAnsweredError && !hasAnswerResponse) {
      if (!sessionId) return;

      if (currentQuestionNumber >= totalQuestions) {
        dispatch(getSessionResult({ session_id: sessionId }));
        return;
      }

      const nextQuestionNumber = currentQuestionNumber + 1;
      setQuestionNumber(nextQuestionNumber);
      setSelected(null);
      dispatch(
        getQuizQuestions({
          session_id: sessionId,
          question_number: nextQuestionNumber,
        })
      );
      return;
    }

    if (isLastQuestion) {
      if (sessionId) {
        dispatch(getSessionResult({ session_id: sessionId }));
      }
      dispatch(getQuizHistory());
      return;
    }

    if (submittedAnswer?.next_question) {
      const nextQuestionNumber = currentQuestionNumber + 1;
      setQuestionNumber(nextQuestionNumber);
      dispatch(setNextQuestion());
      return;
    }

    if (sessionId && currentQuestionNumber >= totalQuestions) {
      dispatch(getSessionResult({ session_id: sessionId }));
      return;
    }

    const nextQuestionNumber = currentQuestionNumber + 1;
    setQuestionNumber(nextQuestionNumber);
    setSelected(null);
    dispatch(
      getQuizQuestions({
        session_id: sessionId,
        question_number: nextQuestionNumber,
      })
    );
  };

  const handleSelectOption = (idx, optionKey) => {
    if (!sessionId || selected !== null || loading || hasAnswerResponse) return;

    setSelected(idx);
    dispatch(clearQuizError());

    dispatch(
      submitAnswer({
        session_id: sessionId,
        question_number: currentQuestionNumber,
        selected_option: optionKey,
      })
    );
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const parsedDate = new Date(value);

    if (Number.isNaN(parsedDate.getTime())) return value;

    return parsedDate.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getOptionText = (question, optionKey) => {
    const option = question?.options?.find(
      (item) => String(item.key).toLowerCase() === String(optionKey).toLowerCase()
    );

    return option?.text || optionKey || "-";
  };

  const resultAttempts = Array.isArray(sessionResult?.attempts)
    ? sessionResult.attempts
    : [];

  const styles = {
    quizWrap: {
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start",
      paddingTop: isMobile ? "20px" : "46px",
      width: "100%",
    },
    examLayout: {
      width: "100%",
      maxWidth: "900px",
    },
    quizCard: {
      background: "#fff",
      borderRadius: "17px",
      width: "100%",
      boxShadow: "0 1px 8px #0001",
      padding: isMobile ? "30px 20px" : "42px 34px",
      boxSizing: "border-box",
      overflow: "hidden",
    },
    backButton: {
      display: "inline-flex",
      alignItems: "center",
      marginBottom: "16px",
      padding: "8px 14px",
      borderRadius: "8px",
      border: "1px solid #D0D5DD",
      background: "#fff",
      color: "#18314F",
      fontWeight: 600,
      fontSize: "14px",
      cursor: "pointer",
    },
    quizHeader: {
      display: "grid",
      gridTemplateColumns: isMobile || !shouldShowMockTime ? "1fr" : "minmax(0, 1fr) minmax(190px, 230px)",
      alignItems: "center",
      gap: isMobile ? "12px" : "18px",
      marginBottom: "24px",
    },
    quizHeadingBlock: {
      minWidth: 0,
    },
    quizTitle: {
      color: "#0F141F",
      fontWeight: 700,
      fontSize: "19px",
      textAlign: "center",
      marginBottom: "8px",
    },
    quizProgress: {
      color: "#0F141F",
      fontWeight: 400,
      fontSize: "15px",
      textAlign: "center",
      marginBottom: 0,
    },
    mockTimer: {
      width: "100%",
      maxWidth: isMobile ? "100%" : "230px",
      boxSizing: "border-box",
      background: "linear-gradient(180deg, #0D355C 0%, #153F68 100%)",
      borderRadius: "10px",
      boxShadow: "0 8px 18px rgba(13, 53, 92, 0.12)",
      padding: isMobile ? "10px" : "11px",
      color: "#fff",
      justifySelf: isMobile ? "stretch" : "end",
    },
    mockTimerHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "8px",
      marginBottom: isMobile ? "8px" : "9px",
    },
    mockTimerLabel: {
      fontSize: isMobile ? "11px" : "11px",
      fontWeight: 700,
      letterSpacing: "0",
      textTransform: "uppercase",
      color: "#DDEBFF",
    },
    mockTimerIcon: {
      width: isMobile ? "24px" : "26px",
      height: isMobile ? "24px" : "26px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(255, 255, 255, 0.14)",
      border: "1px solid rgba(255, 255, 255, 0.24)",
      fontSize: "15px",
    },
    mockTimerDigits: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: "5px",
    },
    mockTimerItem: {
      minWidth: 0,
      border: "1px solid rgba(255, 255, 255, 0.18)",
      borderRadius: "7px",
      background: "rgba(255, 255, 255, 0.12)",
      padding: isMobile ? "7px 3px" : "7px 4px",
      textAlign: "center",
    },
    mockTimerValue: {
      display: "block",
      fontSize: isMobile ? "clamp(17px, 5.4vw, 20px)" : "20px",
      fontWeight: 800,
      lineHeight: 1,
      fontVariantNumeric: "tabular-nums",
    },
    mockTimerUnit: {
      display: "block",
      color: "#DDEBFF",
      fontSize: "8px",
      fontWeight: 700,
      marginTop: "5px",
    },
    progressBar: {
      width: "100%",
      height: "4px",
      background: "#C1D4FA",
      borderRadius: "5px",
      marginBottom: "34px",
      overflow: "hidden",
    },
    progressBarFill: {
      height: "100%",
      background: "#5E7EB7",
      borderRadius: "5px",
      transition: "width 0.2s",
      width: totalQuestions ? `${(currentQuestionNumber / totalQuestions) * 100}%` : "0%",
    },
    quizQuestion: {
      fontWeight: 500,
      color: "#0F141F",
      fontSize: "18px",
      marginBottom: "20px",
    },
    questionImage: {
      display: "block",
      maxWidth: "100%",
      height: "auto",
      margin: "0 0 20px",
      borderRadius: "8px",
      border: "1px solid #E3E8F7",
      background: "#fff",
    },
    option: {
      border: "1.5px solid #E3E8F7",
      borderRadius: "8px",
      padding: "13px 18px",
      marginBottom: "12px",
      fontSize: "15.5px",
      fontWeight: 400,
      color: "#0F141E",
      background: "#FAFCFF",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "13px",
      transition: "all 0.2s",
      lineHeight: 1.4,
      overflowWrap: "anywhere",
    },
    optionCircle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "28px",
      height: "28px",
      borderRadius: "50%",
      border: "1.5px solid #0D355C",
      color: "#0D355C",
      background: "#fff",
      fontWeight: 700,
      fontSize: "16px",
      flexShrink: 0,
    },
    explanation: {
      borderRadius: "10px",
      padding: "16px 18px",
      fontSize: "15px",
      marginTop: "24px",
      background: "#FAFCFF",
      border: "1.5px solid #E3E8F7",
    },
    explanationHighlight: {
      color: "#47D834",
      fontWeight: 600,
      marginBottom: "4px",
    },
    nextBtn: {
      background: "#0D355C",
      color: "#fff",
      fontWeight: 600,
      border: "none",
      borderRadius: "12px",
      padding: isMobile ? "12px 18px" : "11px 34px",
      fontSize: "16px",
      cursor: "pointer",
      opacity: selected === null || !canContinue ? 0.5 : 1,
      width: isMobile ? "100%" : "auto",
      minWidth: isMobile ? 0 : "120px",
    },
    nextBtnRow: {
      display: "flex",
      justifyContent: isMobile ? "stretch" : "flex-end",
      marginTop: "20px",
      width: "100%",
      clear: "both",
    },
    resultGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
      gap: "12px",
      marginTop: "20px",
    },
    resultBox: {
      border: "1px solid #E3E8F7",
      borderRadius: "8px",
      padding: "14px",
      background: "#FAFCFF",
    },
    resultLabel: {
      color: "#6B7A90",
      fontSize: "13px",
      marginBottom: "4px",
    },
    resultValue: {
      color: "#0F141F",
      fontSize: "20px",
      fontWeight: 700,
    },
    resultCard: {
      maxWidth: "760px",
    },
    resultHeader: {
      display: "flex",
      justifyContent: "space-between",
      gap: "12px",
      alignItems: "center",
      flexWrap: "wrap",
    },
    statusBadge: {
      borderRadius: "16px",
      padding: "5px 12px",
      fontSize: "13px",
      fontWeight: 700,
    },
    resultMeta: {
      color: "#6B7A90",
      fontSize: "14px",
      marginTop: "12px",
      lineHeight: 1.6,
    },
    attemptsTitle: {
      color: "#0F141F",
      fontSize: "17px",
      fontWeight: 700,
      marginTop: "28px",
      marginBottom: "12px",
    },
    attemptCard: {
      border: "1px solid #E3E8F7",
      borderRadius: "8px",
      background: "#FAFCFF",
      padding: "16px",
      marginBottom: "12px",
    },
    attemptTopRow: {
      display: "flex",
      justifyContent: "space-between",
      gap: "12px",
      alignItems: "center",
      marginBottom: "10px",
    },
    attemptQuestion: {
      color: "#0F141F",
      fontWeight: 600,
      fontSize: "15px",
      lineHeight: 1.45,
      marginBottom: "12px",
    },
    attemptAnswer: {
      color: "#0F141F",
      fontSize: "14px",
      lineHeight: 1.55,
    },
    attemptExplanation: {
      color: "#4B5D78",
      fontSize: "14px",
      lineHeight: 1.5,
      marginTop: "10px",
    },
  };

  const goBack = () => {
    setPage("bank");
  };

  const backButton = (
    <button type="button" onClick={goBack} style={styles.backButton}>
      ← Back
    </button>
  );

  if (sessionResult?.total_questions || sessionResult?.score !== undefined) {
    return (
      <div style={styles.quizWrap}>
        <div style={{ ...styles.quizCard, ...styles.resultCard }}>
          {backButton}
          <div style={styles.resultHeader}>
            <div>
              <div style={{ ...styles.quizTitle, textAlign: "left", marginBottom: 0 }}>
                {sessionResult?.topic_name || "Quiz Result"}
              </div>
              <div style={{ ...styles.quizProgress, textAlign: "left", marginBottom: 0 }}>
                {sessionResult?.session_type || "quiz"} - {sessionResult?.status || "completed"}
              </div>
            </div>
            <span
              style={{
                ...styles.statusBadge,
                color: sessionResult?.passed ? "#177A38" : "#B42318",
                background: sessionResult?.passed ? "#EAF8EE" : "#FEF3F2",
              }}
            >
              {sessionResult?.passed ? "Passed" : "Not Passed"}
            </span>
          </div>
          <div style={styles.resultGrid}>
            <div style={styles.resultBox}>
              <div style={styles.resultLabel}>Score</div>
              <div style={styles.resultValue}>
                {sessionResult?.score ?? 0}/{sessionResult?.total_questions ?? totalQuestions}
              </div>
            </div>
            <div style={styles.resultBox}>
              <div style={styles.resultLabel}>Percentage</div>
              <div style={styles.resultValue}>{sessionResult?.score_percent ?? 0}%</div>
            </div>
            <div style={styles.resultBox}>
              <div style={styles.resultLabel}>Duration</div>
              <div style={styles.resultValue}>{sessionResult?.duration_minutes ?? 0} min</div>
            </div>
            <div style={styles.resultBox}>
              <div style={styles.resultLabel}>Questions</div>
              <div style={styles.resultValue}>{sessionResult?.total_questions ?? totalQuestions}</div>
            </div>
          </div>
          <div style={styles.resultMeta}>
            Started: {formatDateTime(sessionResult?.started_at)}
            <br />
            Completed: {formatDateTime(sessionResult?.completed_at)}
          </div>
          {["mock", "practice_test", "final_exam", "quiz", "pre_assessment"].includes(sessionResult?.session_type) && (
            <button
              type="button"
              onClick={() => {
                setQuestionNumber(1);
                setSelected(null);
                const sessionType = sessionResult.session_type;
                const payload = { session_type: sessionType, num_questions: sessionType === "quiz" || sessionType === "pre_assessment" ? 25 : 300 };
                if (sessionType === "mock") payload.domain_id = sessionResult.domain_id;
                if (sessionType === "final_exam") payload.bank_set = sessionResult.mock_exam_id;
                dispatch(startMocKExamSession(payload));
              }}
              style={{
                marginTop: "16px",
                border: 0,
                borderRadius: "999px",
                background: "#17D1A3",
                color: "#fff",
                fontWeight: 700,
                fontSize: "14px",
                padding: "12px 18px",
                cursor: "pointer",
              }}
            >
              Retake
            </button>
          )}
          {resultAttempts.length > 0 && (
            <>
              <div style={styles.attemptsTitle}>Question Review</div>
              {resultAttempts.map((attempt, idx) => {
                const question = attempt.question || {};
                const selectedOption = attempt.selected_option;
                const correctOptionKey = question.correct_option;

                return (
                  <div key={attempt.id || question.id || idx} style={styles.attemptCard}>
                    <div style={styles.attemptTopRow}>
                      <span style={{ color: "#6B7A90", fontSize: "13px", fontWeight: 700 }}>
                        Question {idx + 1}
                      </span>
                      <span
                        style={{
                          ...styles.statusBadge,
                          color: attempt.is_correct ? "#177A38" : "#B42318",
                          background: attempt.is_correct ? "#EAF8EE" : "#FEF3F2",
                        }}
                      >
                        {attempt.is_correct ? "Correct" : "Incorrect"}
                      </span>
                    </div>
                    {question.topic_name && (
                      <div style={{ color: "#0D355C", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>
                        Module: {question.topic_name}
                      </div>
                    )}
                    <div style={styles.attemptQuestion}><QuestionBody text={question.text} /></div>
                    {question.image_url && (
                      <img src={question.image_url} alt="" style={styles.questionImage} />
                    )}
                    <div style={styles.attemptAnswer}>
                      <strong>Your answer:</strong> {String(selectedOption || "-").toUpperCase()} - {cleanText(getOptionText(question, selectedOption))}
                    </div>
                    <div style={styles.attemptAnswer}>
                      <strong>Correct answer:</strong> {String(correctOptionKey || "-").toUpperCase()} - {cleanText(getOptionText(question, correctOptionKey))}
                    </div>
                    {question.explanation && (
                      <div style={styles.attemptExplanation}>
                        <strong>Explanation:</strong> <QuestionBody text={question.explanation} />
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.quizWrap}>
      <div style={styles.examLayout}>
        <div style={styles.quizCard}>
          {backButton}
          <div style={styles.quizHeader}>
            <div style={styles.quizHeadingBlock}>
              <div style={styles.quizTitle}>{title}</div>
              <div style={styles.quizProgress}>
                {currentQuestionNumber} of {totalQuestions}
              </div>
            </div>
            {shouldShowMockTime && (
              <aside style={styles.mockTimer}>
                <div style={styles.mockTimerHeader}>
                  <span style={styles.mockTimerLabel}>Time Remaining</span>
                  <span style={styles.mockTimerIcon} aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </span>
                </div>
                <div style={styles.mockTimerDigits}>
                  {timerParts.map((part) => (
                    <span key={part.label} style={styles.mockTimerItem}>
                      <span style={styles.mockTimerValue}>{part.value}</span>
                      <span style={styles.mockTimerUnit}>{part.label}</span>
                    </span>
                  ))}
                </div>
              </aside>
            )}
          </div>
          <div style={styles.progressBar}>
            <div style={styles.progressBarFill}></div>
          </div>
          {quiz?.session_type === "mock" && questionData.topic_name && (
            <div
              style={{
                display: "inline-block",
                marginBottom: "10px",
                padding: "4px 12px",
                borderRadius: "999px",
                background: "#EEF4FF",
                color: "#0D355C",
                fontSize: "13px",
                fontWeight: 600,
              }}
            >
              Module: {questionData.topic_name}
            </div>
          )}
          <div style={{ ...styles.quizQuestion, ...(error && { color: "#B42318" }) }}>
            {loading && !questionText ? "Loading..." : error || <QuestionBody text={questionText} />}
          </div>
          {questionImageUrl && !error && (
            <img src={questionImageUrl} alt="" style={styles.questionImage} />
          )}
          <div>
            {options.map((opt, idx) => {
              const optionText =
                typeof opt === "string"
                  ? opt
                  : opt?.text ||
                  opt?.option ||
                  opt?.option_text ||
                  opt?.answer ||
                  opt?.label ||
                  opt?.value ||
                  "";
              const optionKey =
                typeof opt === "string"
                  ? String.fromCharCode(97 + idx)
                  : opt?.key || opt?.value || String.fromCharCode(97 + idx);
              const normalizedOptionKey = String(optionKey).toLowerCase();
              const isSelected = selected === idx;
              const isCorrect =
                showImmediateFeedback &&
                correctOption &&
                normalizedOptionKey === String(correctOption).toLowerCase();

              return (
                <div
                  key={optionText || idx}
                  style={{
                    ...styles.option,
                    ...(isSelected && {
                      borderColor: "#22436C",
                      background: "#F1F5FA",
                    }),
                    ...(showImmediateFeedback && hasAnswerResponse && isCorrect && {
                      borderColor: "#17B26A",
                      background: "#ECFDF3",
                      color: "#0F5132",
                    }),
                    ...(showImmediateFeedback && hasAnswerResponse && isSelected && !isCorrect && {
                      borderColor: "#D92D20",
                      background: "#FEF3F2",
                      color: "#7A271A",
                    }),
                    ...(selected !== null && {
                      cursor: "default",
                      pointerEvents: "none",
                    }),
                  }}
                  onClick={() => handleSelectOption(idx, optionKey)}
                >
                  <span
                    style={{
                      ...styles.optionCircle,
                      ...(isSelected && {
                        borderColor: "#22436C",
                        background: "#22436C",
                        color: "#fff",
                      }),
                      ...(showImmediateFeedback && hasAnswerResponse && isCorrect && {
                        borderColor: "#17B26A",
                        background: "#17B26A",
                        color: "#fff",
                      }),
                      ...(showImmediateFeedback && hasAnswerResponse && isSelected && !isCorrect && {
                        borderColor: "#D92D20",
                        background: "#D92D20",
                        color: "#fff",
                      }),
                    }}
                  >
                    {String.fromCharCode(65 + idx)}
                  </span>
                  {optionText}
                </div>
              )
            })}
          </div>
          {selected !== null && (
            <div
              style={{
                ...styles.explanation,
                background: answerFeedbackBg,
                borderColor: answerFeedbackBorder,
              }}
            >
              <div style={{ color: answerFeedbackColor, fontWeight: 700, marginBottom: "4px" }}>
                {loading && !hasAnswerResponse
                  ? "Checking answer..."
                  : submittedAnswer?.is_correct
                    ? "Correct"
                    : "Incorrect"}
              </div>
              {showImmediateFeedback && explanationHighlight && (
                <div style={styles.explanationHighlight}>
                  {explanationHighlight}
                </div>
              )}
              <div style={{ color: error ? "#B42318" : "#0F141F", marginTop: "2px" }}>
                {error ||
                  (showImmediateFeedback && explanation ? (
                    <QuestionBody text={explanation} />
                  ) : (
                    "Explanation will appear after your answer is checked."
                  ))}
              </div>
            </div>
          )}
          <div style={styles.nextBtnRow}>
            <button
              style={{ ...styles.nextBtn }}
              disabled={selected === null || !canContinue}
              onClick={handleNext}
            >
              {isLastQuestion ? "View Result" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ====================================================================
// MAIN EXPORTED COMPONENT
// ====================================================================
export default function QuestionBankApp() {
  const [page, setPage] = useState("bank");
  const { isMobile } = useResponsive();

  const styles = {
    root: {
      fontFamily: "Inter, Arial, sans-serif",
      background: "#ECF2FE",
      minHeight: "100vh",
      padding: isMobile ? "15px" : "20px 40px",
    },
    container: { maxWidth: "950px", margin: "0 auto" },
    breadcrumb: { fontSize: "14px", color: "#8CA1BE", padding: "20px 0 0 0" },
    breadcrumbCurrent: { color: "#22436C" },
  };

  return (
    <div style={styles.root}>
      <div style={styles.container}>
        <div style={styles.breadcrumb}>
          <span style={{ cursor: "pointer" }} onClick={() => setPage("bank")}>
            Home
          </span>{" "}
          &gt;{" "}
          <span
            style={{
              ...styles.breadcrumbCurrent,
              ...(page !== "bank" ? { cursor: "pointer" } : {}),
            }}
            onClick={() => page !== "bank" && setPage("bank")}
          >
            Question Bank
          </span>
          {page === "mock-exams" && (
            <>
              {" "}&gt; <span style={styles.breadcrumbCurrent}>Mock Exams</span>
            </>
          )}
          {page === "final-exams" && (
            <>
              {" "}&gt; <span style={styles.breadcrumbCurrent}>Final Exams</span>
            </>
          )}
          {page === "quiz" && (
            <>
              {" "}&gt; <span style={styles.breadcrumbCurrent}>Mock Quiz</span>
            </>
          )}
        </div>

        {page === "bank" && (
          <QuestionBankLanding setPage={setPage} isMobile={isMobile} />
        )}
        {page === "mock-exams" && (
          <MockExamsHub setPage={setPage} isMobile={isMobile} />
        )}
        {page === "final-exams" && (
          <FinalExamsHub setPage={setPage} isMobile={isMobile} />
        )}
        {page === "quiz" && (
          <ContentGuard>
            <QuizView setPage={setPage} isMobile={isMobile} />
          </ContentGuard>
        )}
      </div>
    </div>
  );
}
